import React from 'react';
import {cn} from '../lib/cn';
import type {MainLoaderProps} from './types';
import {toCssSize} from '../lib/utils';

const loaderSizePixels: Record<NonNullable<MainLoaderProps['size']>, number> = {
  sm: 16,
  md: 32,
  lg: 48,
  xl: 58,
  '2xl': 72,
};

const labelSizeClassName: Record<NonNullable<MainLoaderProps['size']>, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-base',
  '2xl': 'text-base',
};

const CircularLoader = React.memo(
  ({
    visible = true,
    size = 'md',
    height,
    width,
    position,
    label,
    className,
  }: MainLoaderProps) => {
    if (!visible) return null;

    return (
      <div
        className={cn(
          'main-loader flex flex-col items-center justify-center gap-2 z-50',
          position === 'center' && 'h-full',
          className,
        )}
        role="progressbar"
      >
        <svg
          viewBox="25 25 50 50"
          className="main-loader__svg text-[var(--circular-loader-color)]"
          style={{
            width: toCssSize(width ?? loaderSizePixels[size]),
            height: toCssSize(height ?? width ?? loaderSizePixels[size]),
          }}
        >
          <circle
            r="20"
            cy="50"
            cx="50"
            className="cover fill-none stroke-current opacity-5"
            strokeWidth="7"
          />
          <circle
            r="20"
            cy="50"
            cx="50"
            className="circle fill-none stroke-current"
            strokeDasharray="1 200"
            strokeDashoffset="0"
            strokeLinecap="round"
            strokeWidth="7"
          >
            <animateTransform
              attributeName="transform"
              dur="2.25s"
              from="0 50 50"
              repeatCount="indefinite"
              to="360 50 50"
              type="rotate"
            />
            <animate
              attributeName="stroke-dasharray"
              calcMode="spline"
              dur="1.8s"
              keyTimes="0;0.5;1"
              keySplines="0.42 0 0.58 1;0.42 0 0.58 1"
              repeatCount="indefinite"
              values="1 200;90 200;90 200"
            />
            <animate
              attributeName="stroke-dashoffset"
              calcMode="spline"
              dur="1.8s"
              keyTimes="0;0.5;1"
              keySplines="0.42 0 0.58 1;0.42 0 0.58 1"
              repeatCount="indefinite"
              values="0;-35;-125"
            />
          </circle>
        </svg>
        {label && (
          <div
            className={cn(
              labelSizeClassName[size],
              'font-medium text-[var(--circular-loader-label-color)]',
            )}
          >
            {label}
          </div>
        )}
      </div>
    );
  },
);

CircularLoader.displayName = 'CircularLoader';

export {CircularLoader};
